import * as React from 'react';
import { styled } from '@linaria/react';

import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import {
  Svg,
  CodeSnippet,
  Row,
} from './BasicShapesDemo/BasicShapes.shared';

const WIDTH = 280;
const HEIGHT = 280;

function StrokeDashOffsetDemo() {
  const playTick = useTickSfx();

  return (
    <Demo
      id="stroke-dashoffset-basic-demo"
      outputWrapper={OutputWrapper}
      initialValues={{
        dashLength: 10,
        gapLength: 10,
        dashOffset: 0,
      }}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          key="dashOffset"
          id="dashOffset"
          label="Dash Offset"
          min={-40}
          max={40}
          step={1}
          wrapperStyle={{ gridArea: 'dashOffset' }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          key="dashLength"
          id="dashLength"
          label="Dash Size"
          min={0}
          max={40}
          step={0.5}
          wrapperStyle={{ gridArea: 'dashLength' }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          key="gapLength"
          id="gapLength"
          label="Gap Size"
          min={0}
          max={40}
          step={0.5}
          wrapperStyle={{ gridArea: 'gapLength' }}
          onChange={() => {
            playTick();
          }}
        />,
      ]}
    >
      {({ dashLength, gapLength, dashOffset }) => {
        const dashArray = `${dashLength}, ${gapLength}`;
        const code = `\
<style>
  rect {
    stroke: oklch(0.9 0.25 164);
    stroke-width: 5;
    stroke-dasharray: ${dashArray};
    stroke-dashoffset: ${dashOffset};
  }
</style>
`;
        return (
          <StyledRow>
            <StyledCodeSnippet code={code} />

            <SvgWrapper>
              <Svg
                width={WIDTH}
                height={HEIGHT}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              >
                <rect
                  x={WIDTH / 4}
                  y={WIDTH / 4}
                  width={WIDTH / 2}
                  height={WIDTH / 2}
                  rx={10}
                  fill="none"
                  stroke="var(--wham-complementary-500)"
                  strokeWidth={5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={dashArray}
                  strokeDashoffset={dashOffset}
                />
              </Svg>
            </SvgWrapper>
          </StyledRow>
        );
      }}
    </Demo>
  );
}

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const ControlsGrid = styled.div`
  display: grid;
  gap: 16px 32px;
  grid-template-areas:
    'dashOffset'
    'dashLength'
    'gapLength';

  @media (min-width: 22rem) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'dashOffset dashOffset'
      'dashLength gapLength';
  }
  @media (min-width: 42.5rem) {
    grid-template-columns: 2fr 1fr 1fr;
    grid-template-areas: 'dashOffset dashLength gapLength';
  }
`;

const StyledRow = styled(Row)`
  @container (max-width: 37rem) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  max-height: ${HEIGHT}px;
  overflow: hidden;

  @container (max-width: 37rem) {
    width: 100%;
  }
`;

const SvgWrapper = styled.div`
  display: flex;

  @container (max-width: 37rem) {
    position: relative;
  }
`;

export default StrokeDashOffsetDemo;
